import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { payrollLeave } from '../../api';

export const LEAVE_TYPES = [['annual', 'Annual leave'], ['sick', 'Sick leave'], ['family', 'Family responsibility'], ['unpaid', 'Unpaid leave']];
const today = () => new Date().toISOString().slice(0, 10);

export const LeaveDialog = ({ open, onOpenChange, clientId, employees, preset, onSaved, toast }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ employeeId: preset || employees[0]?.id || '', type: 'annual', startDate: today(), endDate: today(), days: '', note: '' }); }, [open, preset]); // eslint-disable-line react-hooks/exhaustive-deps
  const emp = employees.find((e) => e.id === f?.employeeId);
  useEffect(() => {
    if (!f?.startDate || !f?.endDate || !emp) return;
    payrollLeave.workdays(f.startDate, f.endDate, emp.workDaysPerWeek).then((r) => setF((x) => ({ ...x, days: r.days })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f?.startDate, f?.endDate, f?.employeeId]);
  if (!f) return null;
  const bal = emp && f.type !== 'unpaid' ? emp.balances[f.type] : null;
  const over = bal != null && Number(f.days) > bal;

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    payrollLeave.add(clientId, { ...f, days: Number(f.days) || null })
      .then(() => { toast({ title: 'Leave recorded', description: `${emp?.name} · ${f.days} day(s)` }); onOpenChange(false); onSaved(); })
      .catch((err) => toast({ title: 'Could not record leave', description: err?.response?.data?.detail?.toString() || 'Check the fields.', variant: 'destructive' }))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="leave-dialog">
        <DialogHeader><DialogTitle>Record leave</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Employee</Label>
              <Select value={f.employeeId} onValueChange={(v) => setF({ ...f, employeeId: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="leave-employee-select"><SelectValue placeholder="Choose employee"/></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Leave type</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="leave-type-select"><SelectValue/></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`leave-type-${k}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label htmlFor="lv-s" className="text-xs">From</Label><Input id="lv-s" type="date" required value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value, endDate: f.endDate < e.target.value ? e.target.value : f.endDate })} className="mt-1 h-9" data-testid="leave-start-input"/></div>
            <div><Label htmlFor="lv-e" className="text-xs">To</Label><Input id="lv-e" type="date" required min={f.startDate} value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} className="mt-1 h-9" data-testid="leave-end-input"/></div>
            <div><Label htmlFor="lv-d" className="text-xs">Working days</Label><Input id="lv-d" type="number" min="0.5" step="0.5" required value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} className="mt-1 h-9" data-testid="leave-days-input"/></div>
            <div><Label htmlFor="lv-n" className="text-xs">Note</Label><Input id="lv-n" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1 h-9" placeholder="e.g. Doctor's note" data-testid="leave-note-input"/></div>
          </div>
          <p className="text-xs text-stone-500" data-testid="leave-dialog-hint">
            Days exclude weekends and SA public holidays (edit if needed).{' '}
            {f.type === 'unpaid' ? 'Unpaid leave reduces basic salary pro-rata on the pay run.' : bal != null && <span className={over ? 'text-red-700 font-medium' : ''}>Available now: {bal} days{over ? ' — this will go negative.' : '.'}</span>}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !f.employeeId} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="leave-save-btn">{busy && <Loader2 size={14} className="mr-2 animate-spin"/>} Record leave</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
